import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ShieldExclamationIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import { api } from '@/lib/api'
import toast from 'react-hot-toast'

interface SecureScoreEmptyStateProps {
  tenantId: string
}

export function SecureScoreEmptyState({ tenantId }: SecureScoreEmptyStateProps) {
  const queryClient = useQueryClient()
  const [isSyncing, setIsSyncing] = useState(false)

  const syncMutation = useMutation({
    mutationFn: () => api.refreshSecureScore(tenantId),
    onMutate: () => setIsSyncing(true),
    onSuccess: (data) => {
      if (data) {
        queryClient.setQueryData(['secureScoreSummary', tenantId], data)
        toast.success('Secure Score synced successfully')
      } else {
        toast.error('No Secure Score data returned — check tenant permissions')
      }
    },
    onError: () => {
      toast.error('Failed to sync Secure Score')
    },
    onSettled: () => setIsSyncing(false),
  })

  return (
    <div className="bg-dark-800 rounded-2xl border border-dark-700 p-8">
      <div className="text-center">
        <div className="mx-auto w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mb-4 border border-amber-500/30">
          <ShieldExclamationIcon className="w-8 h-8 text-amber-400" />
        </div>
        <h3 className="text-lg font-medium text-slate-100 mb-2">
          Secure Score Not Available
        </h3>
        <p className="text-slate-400 mb-6 max-w-md mx-auto">
          Microsoft Secure Score data is not available for this tenant. This could be because:
        </p>
        <ul className="text-left text-sm text-slate-400 space-y-2 max-w-md mx-auto mb-6">
          <li className="flex items-start gap-2">
            <span className="text-amber-400 mt-1">&bull;</span>
            <span>The tenant doesn't have Microsoft 365 Defender enabled</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-400 mt-1">&bull;</span>
            <span>The app registration may be missing the <code className="bg-dark-700 px-1 rounded text-primary-400">SecurityEvents.Read.All</code> permission</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-400 mt-1">&bull;</span>
            <span>Secure Score data hasn't been generated yet for this tenant</span>
          </li>
        </ul>
        <div className="flex justify-center gap-4">
          <button
            onClick={() => syncMutation.mutate()}
            disabled={isSyncing}
            className="inline-flex items-center justify-center h-9 px-3.5 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap bg-gradient-to-r from-primary-600 to-primary-500 text-white shadow-glow-cyan hover:shadow-glow-cyan-lg hover:-translate-y-0.5 disabled:from-dark-600 disabled:to-dark-600 disabled:shadow-none disabled:cursor-not-allowed"
          >
            <ArrowPathIcon className={`h-4 w-4 mr-1.5 ${isSyncing ? 'animate-spin' : ''}`} />
            {isSyncing ? 'Syncing...' : 'Sync Secure Score'}
          </button>
          <a
            href="https://security.microsoft.com/securescore"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary"
          >
            View in Microsoft Defender Portal
          </a>
        </div>
        <p className="mt-4 text-xs text-slate-500">
          Tenant ID: {tenantId}
        </p>
      </div>
    </div>
  )
}
